import Data from 'data';
import { skillAuraStats, skillCalc, skillFormula, skillPassiveStats } from 'logic/skills/calc';
import skillFunctions from '.';
import { AnimationCalculator, RollbackAnimationCalculator } from '../animation';
import { DamageCalculator, missileDamage } from '../damage';
import { defaultDPS, DPSCalculator, missileDPS } from '../dps';
import { defaultInfo } from '../skillinfo';

const functions = {};

// Dragon Talon

functions[42] = {
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, skill.param8 && stats.base.progressive ? false : true);
    if (skill.finishing) {
      calc.finisher();
    }
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.skillbonus('damagepercent', skillFormula(skill, 'ln12', stats, level));
    calc.kickdamage();
    calc.physical(skillFormula(skill, 'pdns', stats, level), skillFormula(skill, 'pdxs', stats, level));
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    return calc.calculate(target);
  },
  animation(skill, stats, level) {
    const hits = skillFormula(skill, 'clc1', stats, level);
    return (hits > 1 ? new RollbackAnimationCalculator(stats, skill, hits, 100) : new AnimationCalculator(stats, skill)).calculate();
  },
};

// Shock Web

functions[43] = {
  damage(skill, stats, weapon, level, target) {
    return missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target, {frames: -26});
  },
  dps(id, stats, level, target, info) {
    const skill = Data.skills[id];
    const missile = Data.missileByName[skill.srvmissilea];
    if (!missile) return;
    return missileDPS(skill, missile, stats, level, target, info);
  },
};

// Blade Sentinel

functions[44] = {
  info(id, stats, level, target, allStats) {
    const skill = Data.skills[id];
    const missile = Data.missileByName[skill.srvmissilea];
    if (!missile) return;

    const player = allStats.player;
    const tohit = Data.monLvl[player.level][(Data.info.pd2 || target.single ? 'l' : '') + 'th' + ['', 'n', 'h'][player.difficulty]];
    const passive = skillPassiveStats(skill, stats, level);
    passive.tohit = (passive.tohit || 0) + tohit;

    return {
      damage: missileDamage(missile, player, player.weapon1, level, target, {stats: passive}),
      animation: new AnimationCalculator(stats, skill).calculate(),
    };
  },
  dps: () => null,
};

// Traps

functions[45] = {
  info(id, stats, level, target) {
    const skill = Data.skills[id];
    const monster = Data.monsters[skill.summon];
    if (!monster) return;

    const sumstats = {base: {}, skills: {}, base_skills: {}, weapon1: {weaponspeed: 0}, class: skill.summon};
    for (let i = 1; i <= 5; ++i) {
      const sid = skill[`sumskill${i}`];
      if (sid) {
        const slevel = skillCalc(skill, skill[`sumsk${i}calc`], stats, level);
        sumstats.skills[sid] = slevel;
        sumstats.base_skills[sid] = slevel;
      }
    }
    Object.assign(sumstats.base, skillPassiveStats(skill, stats, level));

    const result = [];
    for (let i = 1; i <= 8; ++i) {
      const sid = monster[`skill${i}`];
      if (sid && monster[`sk${i}lvl`]) {
        const level = sumstats.skills[sid] || monster[`sk${i}lvl`];
        const sumskill = Data.skills[sid];
        if (!sumskill) continue;
        const func = skillFunctions[sumskill.srvdofunc || 0];
        if (!func) continue;
        const info = (func.info || defaultInfo).call(func, sid, sumstats, level, target);
        if (info?.damage) {
          info.dps = (func.dps || defaultDPS).call(func, sid, sumstats, level, target, info);
          result.push(info);
        }
      }
    }

    const info = {};
    if (result.length > 1) {
      info.damage = {};
      info.dps = [];
      result.forEach((sk, index) => {
        for (const label in sk.damage) {
          sk.damage[label].label = label;
          sk.damage[label].sumskill = index + 1;
          info.damage[`sk${index}_${label}`] = sk.damage[label];
        }
        if (sk.dps) {
          sk.dps.sumskill = index + 1;
          info.dps.push(sk.dps);
        }
      });
      if (!info.dps.length) delete info.dps;
    } else {
      info.damage = result[0].damage;
      info.dps = result[0].dps;
    }
    info.animation = new AnimationCalculator(stats, skill).calculate();
    return info;
  },
};

// Dragon Claw

functions[46] = {
  damage(skill, stats, weapon, level, target) {
    const calc = new DamageCalculator(stats, weapon, false, skill.param8 && stats.base.progressive ? false : true);
    calc.finisher();
    if (skill.etype) {
      calc.elemental(skill.etype, skillFormula(skill, 'enms', stats, level),
        skillFormula(skill, 'exms', stats, level), skillFormula(skill, 'edln', stats, level));
    }
    calc.skillbonus('damagepercent', skillFormula(skill, 'clc1', stats, level));
    calc.srcdam(skill.srcdam);
    calc.convert(skill.etype, skillFormula(skill, 'clc4', stats, level));
    calc.skillbonus('tohit_percent', skillFormula(skill, 'toht', stats, level));
    return calc.calculate(target);
  },
};

// Cloak of Shadows

functions[47] = {
  buff(id, stats, level) {
    const skill = Data.skills[id];
    return {
      level,
      filter: skill.aurafilter,
      state: skill.aurastate,
      stats: () => skillPassiveStats(skill, stats, level),
      targetState: skill.auratargetstate,
      targetStats: () => skillAuraStats(skill, stats, level),
    };
  },
};

// Blade Fury

functions[48] = {
  damage(skill, stats, weapon, level, target) {
    return missileDamage(Data.missileByName[skill.srvmissilea], stats, weapon, level, target, {
      stats: {tohit_percent: skillFormula(skill, 'toht', stats, level)},
    });
  },
  animation() {},
  dps(id, stats, level, target, info) {
    if (!info?.damage) return;
    const calc = new DPSCalculator();
    calc.basic_damage('damage', info.damage.damage, {fpa: 6, hits: 1});
    return calc.calculate(stats, target);
  },
};

// Shadow Warrior/Master

functions[49] = {
  summon(id, stats, level, target) {
    const skill = Data.skills[id];
    if (!skill.summon) return;

    const extraStats = {};

    extraStats.skill = skillPassiveStats(skill, stats, level);
    const statValue = skillCalc(skill, skill.aurastatcalc2, stats, level);
    if (skill.aurastat1) extraStats.skill[skill.aurastat1] = statValue;
    if (skill.aurastat2) extraStats.skill[skill.aurastat2] = statValue;
    if (skill.aurastat3) extraStats.skill[skill.aurastat3] = statValue;
    if (skill.aurastat4) extraStats.skill[skill.aurastat4] = statValue;
    if (skill.aurastat5) extraStats.skill[skill.aurastat5] = statValue;
    if (skill.aurastat6) extraStats.skill[skill.aurastat6] = statValue;
  
    if (skill.aurastate) {
      extraStats.skill[`state#${skill.aurastate}`] = 1;
    }

    const skills = {};
    for (const id in stats.skills) {
      if (Data.skills[id].charclass === 'ass') {
        skills[id] = Math.max(1, Math.floor(stats.skills[id] / 2) + Math.floor(level / 3));
      }
    }

    return {
      monster: skill.summon.toLowerCase(),
      level: stats.level,
      group: skill.pettype,

      skill: id,
      skillLevel: level,
      itemLevel: skillFormula(skill, 'ln56', stats, level),

      life_percent: skillFormula(skill, 'clc1', stats, level),
      stats: extraStats,
      skills,
    };
  },
};

export default functions;
